import { Connection, PublicKey, Transaction, TransactionInstruction, LAMPORTS_PER_SOL } from '@solana/web3.js'
import {
  getAssociatedTokenAddress,
  createAssociatedTokenAccountIdempotentInstruction,
  createCloseAccountInstruction,
  TOKEN_PROGRAM_ID,
  NATIVE_MINT,
} from '@solana/spl-token'
import { CpAmm, getUnClaimLpFee } from '@meteora-ag/cp-amm-sdk'
import { createDammV2Program } from '@meteora-ag/dynamic-bonding-curve-sdk'
import { withRpcFallback } from '@/lib/solana'

export interface DammV2PoolLabel {
  dammPoolAddress: string
  label: string
  symbol: string
}

export interface DammV2PositionFees {
  info: {
    label: string
    symbol: string
    dammPoolAddress: string
    positionAddress: string
  }
  // The on-chain token account holding the position NFT. For locked positions
  // this is an escrow PDA, not the owner's ATA.
  positionNftAccount: string
  claimableFeeA: number  // token A lamports
  claimableFeeB: number  // SOL lamports (token B = WSOL)
  claimableFeeADisplay: string
  claimableFeeBDisplay: string
  tokenAMint: string
  tokenBMint: string
  tokenAVault: string
  tokenBVault: string
  hasNft: boolean
}

/**
 * Discover treasury positions on-chain and compute claimable fees.
 *
 * Uses CpAmm.getUserPositionByPool which resolves both unlocked ATAs and
 * lock-escrow PDAs, fixing the previous bug where locked position NFTs were
 * reported as "NFT missing".
 */
export async function fetchDammV2PositionFees(
  connection: Connection,
  treasuryAddress: PublicKey,
  pools: DammV2PoolLabel[],
): Promise<DammV2PositionFees[]> {
  const cpAmm = new CpAmm(connection)
  const results: DammV2PositionFees[] = []

  for (const pool of pools) {
    try {
      const poolPubkey = new PublicKey(pool.dammPoolAddress)

      // Discover treasury positions in this pool (handles lock escrow PDAs).
      const userPositions = await cpAmm.getUserPositionByPool(poolPubkey, treasuryAddress)
      console.log(`[DammV2] ${pool.label}: found ${userPositions.length} position(s) in pool ${pool.dammPoolAddress}`)
      if (userPositions.length === 0) continue

      // Fetch pool state for fee calculation and vault/mint addresses.
      const poolState = await cpAmm.fetchPoolState(poolPubkey)

      for (const { positionNftAccount, position, positionState } of userPositions) {
        console.log(`[DammV2] ${pool.label}: position=${position.toBase58()}, nftAccount=${positionNftAccount.toBase58()}`)

        // Compute claimable fees via SDK helper (replaces manual checkpoint math).
        const { feeTokenA, feeTokenB } = getUnClaimLpFee(poolState, positionState)

        const claimableANum = feeTokenA.isNeg() ? 0 : Number(feeTokenA.toString())
        const claimableBNum = feeTokenB.isNeg() ? 0 : Number(feeTokenB.toString())

        const tokenAMint = (poolState.tokenAMint as PublicKey).toBase58()
        const tokenBMint = (poolState.tokenBMint as PublicKey).toBase58()
        const tokenAVault = (poolState.tokenAVault as PublicKey).toBase58()
        const tokenBVault = (poolState.tokenBVault as PublicKey).toBase58()

        results.push({
          info: {
            label: pool.label,
            symbol: pool.symbol,
            dammPoolAddress: pool.dammPoolAddress,
            positionAddress: position.toBase58(),
          },
          positionNftAccount: positionNftAccount.toBase58(),
          claimableFeeA: claimableANum,
          claimableFeeB: claimableBNum,
          claimableFeeADisplay: claimableANum > 0 ? `${(claimableANum / 1e6).toFixed(2)} tokens` : '0',
          claimableFeeBDisplay: `${(claimableBNum / LAMPORTS_PER_SOL).toFixed(6)} SOL`,
          tokenAMint,
          tokenBMint,
          tokenAVault,
          tokenBVault,
          hasNft: true, // SDK only returns positions where the NFT was found.
        })
      }
    } catch (err) {
      console.error(`[DammV2] Failed to fetch fees for ${pool.label}:`, err)
      // Don't push error rows: if on-chain discovery fails, the position
      // simply won't appear. This is preferable to showing a broken row
      // with "Error" values and a disabled Claim button.
    }
  }

  return results
}

/**
 * Build a claimPositionFee transaction for a DAMM V2 position.
 *
 * Accepts the on-chain positionNftAccount directly (resolved by
 * fetchDammV2PositionFees) instead of deriving an ATA. This correctly handles
 * locked positions where the NFT lives in an escrow PDA.
 *
 * Includes:
 * - ATA creation pre-instructions (token A + WSOL if missing)
 * - The claimPositionFee instruction
 * - WSOL unwrap post-instruction (closeAccount converts WSOL ATA to native SOL)
 */
export async function buildClaimDammV2PositionFeeTransaction(
  connection: Connection,
  owner: PublicKey,
  poolAddress: PublicKey,
  positionAddress: PublicKey,
  positionNftAccount: PublicKey,
  tokenAMint: PublicKey,
  tokenBMint: PublicKey,
  tokenAVault: PublicKey,
  tokenBVault: PublicKey,
): Promise<Transaction> {
  const program = createDammV2Program(connection)

  const ownerTokenA = await getAssociatedTokenAddress(tokenAMint, owner)
  const ownerTokenB = await getAssociatedTokenAddress(tokenBMint, owner)

  // Pre-instructions: create ATAs if they don't exist
  const preInstructions: TransactionInstruction[] = []

  const [ownerTokenAInfo, ownerTokenBInfo] = await withRpcFallback(
    'dammV2.getMultipleAccountsInfo',
    (c) => c.getMultipleAccountsInfo([ownerTokenA, ownerTokenB]),
  )

  if (!ownerTokenAInfo) {
    preInstructions.push(
      createAssociatedTokenAccountIdempotentInstruction(owner, ownerTokenA, owner, tokenAMint)
    )
  }
  if (!ownerTokenBInfo) {
    preInstructions.push(
      createAssociatedTokenAccountIdempotentInstruction(owner, ownerTokenB, owner, tokenBMint)
    )
  }

  // Build the claim instruction. Pass positionNftAccount directly: for locked
  // positions this is the escrow token account, for unlocked it's the ATA.
  const claimIx = await program.methods
    .claimPositionFee()
    .accountsPartial({
      pool: poolAddress,
      position: positionAddress,
      tokenAAccount: ownerTokenA,
      tokenBAccount: ownerTokenB,
      tokenAVault,
      tokenBVault,
      tokenAMint,
      tokenBMint,
      positionNftAccount,
      owner,
      tokenAProgram: TOKEN_PROGRAM_ID,
      tokenBProgram: TOKEN_PROGRAM_ID,
    })
    .instruction()

  // Post-instructions: unwrap WSOL to native SOL
  const postInstructions: TransactionInstruction[] = []
  if (tokenBMint.equals(NATIVE_MINT)) {
    postInstructions.push(
      createCloseAccountInstruction(ownerTokenB, owner, owner)
    )
  }

  // Compose final transaction
  const tx = new Transaction()
  for (const ix of preInstructions) tx.add(ix)
  tx.add(claimIx)
  for (const ix of postInstructions) tx.add(ix)

  return tx
}
